/**
 * OPS_WORKSPACE_LIST: workspace 横断ヘルスチェック画面（Phase 2 / issue #196）。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退）。
 *
 * 設計: spec/ai-inbox-core/admin-ops/wireframes.md §4
 *
 * 守るライン:
 *   - PII を表示しない（個人 email / 氏名 / 電話は API レスポンスにも含まない）
 *   - 行クリック → /workspaces/:id（Phase 4 で実装）
 *
 * PC 専用 (min-width: 1024px、wireframes.md §Open Q3 確定)。
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { BillingStatus, HealthFilterKey, WorkspaceListParams } from '../../lib/workspaces-api';
import { fetchWorkspaces, paramsFromUrlSearch, paramsToUrlSearch } from '../../lib/workspaces-api';
import { WorkspaceFiltersBar } from './WorkspaceFiltersBar';
import { WorkspaceHealthSummary } from './WorkspaceHealthSummary';
import { WorkspaceTable } from './WorkspaceTable';

export interface WorkspaceListPageProps {
  apiBaseUrl: string;
}

export function WorkspaceListPage({ apiBaseUrl }: WorkspaceListPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo<WorkspaceListParams>(
    () => paramsFromUrlSearch(searchParams),
    [searchParams],
  );

  const query = useQuery({
    queryKey: ['admin-ops-workspaces', params],
    queryFn: () => fetchWorkspaces(apiBaseUrl, params),
  });

  const handleParamsChange = (next: WorkspaceListParams) => {
    setSearchParams(paramsToUrlSearch(next));
  };

  // PC 専用ガード（min-width: 1024px 未満は画面に注記）
  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="hidden lg:block">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <header className="mb-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              ALYS Internal / admin-ops
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">
              workspace 横断ヘルスチェック
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              OPS-WS-1〜4: workspace 健康状態を 1 画面で把握。 PII は表示されません（集約値のみ）。
            </p>
          </header>

          <WorkspaceFiltersBar params={params} onChange={handleParamsChange} />

          {query.data && (
            <WorkspaceHealthSummary
              distribution={query.data.billingDistribution}
              sourceStatus={query.data.sourceStatus}
              total={query.data.total}
            />
          )}

          <section className="mt-6">
            {query.isLoading && <p className="text-sm text-slate-500">読み込み中…</p>}
            {query.isError && (
              <p className="text-sm text-rose-600">
                API 取得に失敗しました: {(query.error as Error).message}
              </p>
            )}
            {query.data && (
              <WorkspaceTable
                rows={query.data.rows}
                total={query.data.total}
                page={params.page ?? 1}
                pageSize={params.pageSize ?? 50}
                sort={params.sort}
                order={params.order}
                onParamsChange={handleParamsChange}
                params={params}
              />
            )}
          </section>
        </div>
      </div>

      <div className="block lg:hidden">
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-lg font-semibold text-slate-900">PC 専用画面</h1>
          <p className="mt-3 text-sm text-slate-600">
            admin-ops は最小 1024px の画面幅でのみ動作します（情報密度が高いため）。 PC
            ブラウザでアクセスしてください。
          </p>
        </div>
      </div>
    </main>
  );
}

// 公開: テスト容易性のため
export type { BillingStatus, HealthFilterKey };
